import styled from 'styled-components';
import {
  color,
  container,
  fontFamily,
  fontSize,
  fontWeight,
  radius,
  spacing
} from 'theme';

export const StyledSection = styled.div`
  padding: ${spacing('xl5')} ${spacing('md')};
  max-width: ${container('xl')};
  width: 100%;
  margin: auto;
  text-align: center;
`;

export const StyledFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: ${spacing('xl2')};
  margin-bottom: ${spacing('xl2')};
  gap: ${spacing('xs')};
`;

export const StyledMainFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${spacing('xs')};
`;

export const StyledTrigger = styled.button`
  padding: ${spacing('xs')} ${spacing('md')};
  border: 2px solid ${color('midnight_200')};
  border-radius: ${radius('full')};
  display: flex;
  align-items: center;
  gap: ${spacing('xs')};
  font-size: ${fontSize('sm')};
  font-weight: ${fontWeight('extraBold')};
  background: transparent;
  cursor: pointer;

  svg {
    width: ${spacing('md')} !important;
  }
`;

export const StyledFilter = styled.button`
  padding: ${spacing('xs')} ${spacing('md')};
  border: 2px solid ${color('midnight_200')};
  border-radius: ${radius('full')};
  background-color: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${spacing('xs')};
  font-size: ${fontSize('sm')};
  font-weight: ${fontWeight('extraBold')};

  img {
    height: ${spacing('md')};
    width: auto;
  }

  &.is-active {
    border-color: ${color('navy_800')};
    background-color: ${color('active')};
    color: ${color('text_over_active')};
  }
`;

export const StyledFiltersDropdown = styled.div`
  position: relative;
`;

export const StyledProcesses = styled.div`
  margin-top: ${spacing('xl')};
  margin-bottom: ${spacing('xl')};

  .slick-slider {
    padding-bottom: 0;
    padding-top: calc(${spacing('xl2')} + ${spacing('md')});
  }

  .slick-track {
    display: flex;
    gap: ${spacing('xl')};
  }

  .slick-dots {
    bottom: unset;
    top: 0;
  }
`;

export const StyledProcess = styled.div`
  min-width: 18rem;
  display: flex !important;
  flex-direction: column;
`;

export const StyledBadge = styled.div`
  display: flex;
  align-self: center;
  align-items: center;
  gap: ${spacing('xs')};
  padding: ${spacing('md')} calc(${spacing('xl3')} + ${spacing('xs')})
    ${spacing('md')} ${spacing('lg')};
  margin-bottom: ${spacing('mdlg')};
  background-color: ${color('midnight_100')};
  border-radius: ${radius('full')};
  font-weight: ${fontWeight('extraBold')};
  position: relative;

  img {
    height: ${spacing('lg')};
    display: block;
  }

  button {
    position: absolute;
    right: ${spacing('md')};
    background-color: transparent;
    border: 0;
    padding: ${spacing('sm')};
    width: ${spacing('xl')};
    height: ${spacing('xl')};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: ${radius('full')};
    transition: 0.3s;
    cursor: pointer;

    &:hover {
      background-color: ${color('midnight_200')};
    }

    svg {
      width: ${spacing('lg')} !important;
      height: ${spacing('lg')};
    }
  }
`;

export const StyledSource = styled.div`
  padding: ${spacing('md')};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing('xs')};
  background-color: ${color('midnight_100')};
  font-weight: ${fontWeight('extraBold')};
  font-size: ${fontSize('sm')};

  svg {
    color: ${color('text_subtle')};
    width: ${spacing('md')} !important;
    height: ${spacing('md')};
  }

  span {
    cursor: pointer;
    color: ${color('link')};

    &:hover {
      color: ${color('link_hover')};
    }
  }
`;

export const StyledStepDays = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing('xs2')};
  font-size: ${fontSize('xs')};
  line-height: 1.333;
  font-weight: ${fontWeight('medium')};
  margin-bottom: ${spacing('md')};

  svg {
    width: ${spacing('md')} !important;
    height: ${spacing('md')};
    color: ${color('navy_600')};
  }
`;

export const StyledStepConversion = styled.div`
  margin-left: ${spacing('lg')};
  margin-right: ${spacing('lg')};
  margin-top: ${spacing('lg')};
  padding-top: ${spacing('sm')};
  border-top: 2px solid ${color('white')};
  font-size: ${fontSize('xs')};
  line-height: 1.333;
  font-weight: ${fontWeight('medium')};
  text-transform: uppercase;
  color: ${color('navy_500')};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing('xs')};
`;

export const StyledStepConversionNumber = styled.div`
  width: 3.25rem;
  height: 3.25rem;
  border-radius: ${radius('full')};
  background-color: ${color('navy_900')};
  border: 2px solid ${color('white')};
  color: ${color('text_white')};
  font-family: ${fontFamily('serif')};
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize('md')};
  line-height: 1.25;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const StyledStep = styled.div`
  padding: ${spacing('xl4')} ${spacing('xl')} ${spacing('xl')};
  min-height: 16.75rem;
  position: relative;
  font-size: ${fontSize('sm')};
  line-height: 1.428;

  &::after {
    content: '';
    position: absolute;
    bottom: -${spacing('xl')};
    left: 0;
    right: 0;
    height: ${spacing('xl')};
    clip-path: polygon(0 0, 100% 0, 50% 100%);
    z-index: 1;
    transform: translateY(-0.5px);
  }

  &:first-of-type {
    background-color: ${color('navy_100')};
    padding-top: ${spacing('xl')};

    &::after {
      background-color: ${color('navy_100')};
    }
  }

  &:nth-of-type(2) {
    background-color: ${color('navy_150')};

    &::after {
      background-color: ${color('navy_150')};
    }
  }

  &:nth-of-type(3) {
    background-color: ${color('navy_200')};

    &::after {
      background-color: ${color('navy_200')};
    }
  }

  &:nth-of-type(4) {
    background-color: ${color('navy_300')};

    &::after {
      background-color: ${color('navy_300')};
    }
  }

  &:nth-of-type(5) {
    background-color: ${color('navy_500')};
    color: ${color('text_white')};

    &::after {
      background-color: ${color('navy_500')};
    }

    ${StyledStepDays} svg {
      color: ${color('navy_200')};
    }

    ${StyledStepConversion} {
      color: ${color('text_white')};
    }
  }

  &:nth-of-type(6) {
    background-color: ${color('navy_600')};
    color: ${color('text_white')};

    &::after {
      background-color: ${color('navy_600')};
    }

    ${StyledStepDays} svg {
      color: ${color('navy_200')};
    }

    ${StyledStepConversion} {
      color: ${color('text_white')};
    }
  }

  &:nth-of-type(7) {
    background-color: ${color('navy_700')};
    color: ${color('text_white')};

    &::after {
      background-color: ${color('navy_700')};
    }

    ${StyledStepDays} svg {
      color: ${color('navy_200')};
    }

    ${StyledStepConversion} {
      color: ${color('text_white')};
    }
  }
`;

export const StyledStepTitle = styled.h3`
  margin-top: 0;
  margin-bottom: ${spacing('md')};
  font-family: ${fontFamily('display')};
  font-size: ${fontSize('xl2')};
  line-height: 1;
  font-weight: ${fontWeight('semiBold')};
  text-transform: uppercase;
`;

export const StyledHired = styled.div`
  background-color: ${color('white')};
  padding: ${spacing('xl4')} ${spacing('md')} ${spacing('xl')};
  border-bottom: ${spacing('xs2')} solid ${color('emerald_400')};
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: ${fontSize('sm')};
  text-transform: uppercase;
  font-weight: ${fontWeight('extraBold')};

  svg {
    color: ${color('emerald_500')};
    width: ${spacing('xl3')} !important;
    height: ${spacing('xl3')};
  }

  h4 {
    margin: 0;
    font-family: ${fontFamily('display')};
    font-size: ${fontSize('xl2')};
    line-height: 1;
    font-weight: ${fontWeight('semiBold')};
    text-transform: uppercase;
    color: ${color('emerald_700')};
  }
`;

export const StyledEmpty = styled.div`
  max-width: ${container('sm')};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing('md')};
  text-align: center;
  margin: ${spacing('xl5')} auto;

  svg {
    color: ${color('midnight_300')};
    width: ${spacing('xl4')} !important;
    height: ${spacing('xl4')};
  }

  p {
    margin: 0;
    font-size: ${fontSize('sm')};
  }
`;

export const StyledEmptyTitle = styled.h5`
  font-size: ${fontSize('lg')};
  line-height: 1.2;
  font-weight: ${fontWeight('extraBold')};
  margin: 0;
  color: ${color('text_subtle')};
`;
